import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import type { Announcement } from "~/services/announcementService";
import {
  getAnnouncements,
  dismissAnnouncement,
  getDismissedAnnouncements,
} from "~/services/announcementService";
import { useBranch } from "~/contexts/BranchContext";

const AnnouncementModal = () => {
  const { t, i18n } = useTranslation();
  const { branch, theme } = useBranch();
  const [currentAnnouncement, setCurrentAnnouncement] =
    useState<Announcement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchActiveAnnouncements = async () => {
      if (!branch?.id) {
        return;
      }

      
      const response = await getAnnouncements({
        branch_id: branch.id,
        is_active: true,
        disable_pagination: true,
      });


      if (!response) {
        return;
      }

      if (!response.success) {
        return;
      }

      if (!response.data || response.data.length === 0) {
        return;
      }

      const dismissedIds = getDismissedAnnouncements();
      
      // Find the first non-dismissed announcement
      const activeAnnouncement = response.data.find(
        (announcement) => !dismissedIds.includes(announcement.id)
      );

      if (activeAnnouncement) {
        setCurrentAnnouncement(activeAnnouncement);
        setIsVisible(true);
      } else {
      }
    };

    fetchActiveAnnouncements();
  }, [branch?.id]);

  const handleDismiss = () => {
    if (currentAnnouncement) {
      dismissAnnouncement(currentAnnouncement.id);
      setIsVisible(false);
      setCurrentAnnouncement(null);
    }
  };

  const renderContent = () => {
    if (!currentAnnouncement) {
      return null;
    }

    const isArabic = i18n.language === "ar";
    const { media_type, media_url, content, ar } = currentAnnouncement;
    
    // Use Arabic content if available and language is Arabic
    const displayContent = isArabic && ar?.content ? ar.content : content;
    const displayMediaType = isArabic && ar?.media_type ? ar.media_type : media_type;
    const displayMediaUrl = isArabic && ar?.media_url ? ar.media_url : media_url;
    const displayType = isArabic && ar?.type ? ar.type : currentAnnouncement.type;

    // Render based on media type
    switch (displayMediaType) {

      case "image":
        return (
          <div className="relative w-full">
            {displayMediaUrl && (
              <img
                src={displayMediaUrl}
                alt={displayContent.title || "Announcement"}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg overflow-hidden"
              />
            )}
            {(displayContent.title || displayContent.description) && (
              <div className="mt-4 text-center">
                {displayContent.title && (
                  <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: theme?.titleFontFamily }}>
                    {displayContent.title}
                  </h2>
                )}
                {displayContent.discount && (
                  <p className="text-3xl font-extrabold text-red-600 mb-2" style={{ fontFamily: theme?.titleFontFamily }}>
                    {displayContent.discount}
                  </p>
                )}
                {displayContent.description && (
                  <p className="text-gray-600" style={{ fontFamily: theme?.fontFamily }}>{displayContent.description}</p>
                )}
              </div>
            )}
          </div>
        );

      case "video":
        return (
          <div className="relative w-full">
            {displayMediaUrl && (
              <video
                src={displayMediaUrl}
                controls
                autoPlay
                muted
                loop
                className="w-full h-auto max-h-[70vh] rounded-lg"
              >
                Your browser does not support the video tag.
              </video>
            )}
            {(displayContent.title || displayContent.description) && (
              <div className="mt-4 text-center">
                {displayContent.title && (
                  <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: theme?.titleFontFamily }}>
                    {displayContent.title}
                  </h2>
                )}
                {displayContent.discount && (
                  <p className="text-3xl font-extrabold text-red-600 mb-2" style={{ fontFamily: theme?.titleFontFamily }}>
                    {displayContent.discount}
                  </p>
                )}
                {displayContent.description && (
                  <p className="text-gray-600" style={{ fontFamily: theme?.fontFamily }}>{displayContent.description}</p>
                )}
              </div>
            )}
          </div>
        );

      case "text":
      default:
        return (
          <div className="text-center py-8">
            {displayContent.title && (
              <h2 className="text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: theme?.titleFontFamily }}>
                {displayContent.title}
              </h2>
            )}
            {displayContent.discount && (
              <p className="text-4xl font-extrabold text-red-600 mb-4" style={{ fontFamily: theme?.titleFontFamily }}>
                {displayContent.discount}
              </p>
            )}
            {displayContent.description && (
              <p className="text-lg text-gray-600 whitespace-pre-line" style={{ fontFamily: theme?.fontFamily }}>
                {displayContent.description}
              </p>
            )}
          </div>
        );
    }
  };

  const isArabic = i18n.language === "ar";

  return (
    <AnimatePresence>
      {isVisible && currentAnnouncement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={handleDismiss}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors shadow-lg"
              aria-label="Close announcement"
            >
              <IoClose className="w-6 h-6 text-gray-700" />
            </button>

            {/* Content */}
            <div className="p-6">
              {renderContent()}
            </div>

            {/* Type Badge */}
            <div className="absolute top-4 left-4">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                  currentAnnouncement.type === "sale"
                    ? "bg-red-100 text-red-700"
                    : currentAnnouncement.type === "gift"
                      ? "bg-green-100 text-green-700"
                      : currentAnnouncement.type === "event"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-700"
                }`}
                style={{ fontFamily: theme?.fontFamily }}
              >
                {isArabic && currentAnnouncement.ar?.type ? currentAnnouncement.ar.type : currentAnnouncement.type}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementModal;
